import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { AlertTriangle } from "lucide-react";

/**
 * ConfirmDeleteModal
 * Requires the user to type the entity name verbatim (case-insensitive, trimmed)
 * before enabling the destructive action.
 */
export function ConfirmDeleteModal({
  open,
  onOpenChange,
  entityName,
  entityLabel = "el elemento",
  description,
  cascade = [],
  onConfirm,
  testId,
}) {
  const [typed, setTyped] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => { if (open) { setTyped(""); setBusy(false); } }, [open]);

  const match = typed.trim().toLowerCase() === String(entityName || "").trim().toLowerCase();

  const submit = async () => {
    if (!match || busy) return;
    setBusy(true);
    try {
      await onConfirm();
      onOpenChange(false);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid={testId}>
        <DialogHeader>
          <div className="flex items-start gap-3">
            <div className="w-9 h-9 rounded-full bg-red-100 flex items-center justify-center shrink-0">
              <AlertTriangle className="w-5 h-5 text-red-600" />
            </div>
            <div className="flex-1 min-w-0">
              <DialogTitle className="text-[#0F172A]">Eliminar {entityLabel}</DialogTitle>
              <DialogDescription className="mt-1 text-sm text-[#64748B]">
                {description || "Esta acción es permanente y no se puede deshacer."}
              </DialogDescription>
            </div>
          </div>
        </DialogHeader>

        {cascade.length > 0 && (
          <div className="rounded-lg bg-red-50 border border-red-100 p-3 space-y-1">
            <p className="text-xs font-semibold text-red-700">Se eliminará también:</p>
            <ul className="text-xs text-red-700 space-y-0.5 pl-4 list-disc">
              {cascade.map((c) => <li key={c}>{c}</li>)}
            </ul>
          </div>
        )}

        <div className="space-y-2">
          <Label className="text-sm text-[#0F172A]">
            Escribe <span className="font-mono font-bold bg-gray-100 px-1.5 py-0.5 rounded text-[#1B4D3E]">{entityName}</span> para confirmar
          </Label>
          <Input
            value={typed}
            onChange={(e) => setTyped(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter" && match) submit(); }}
            placeholder={entityName}
            data-testid={testId ? `${testId}-input` : undefined}
            autoFocus
          />
        </div>

        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={busy}>
            Cancelar
          </Button>
          <Button
            onClick={submit}
            disabled={!match || busy}
            className="bg-red-600 hover:bg-red-700 text-white disabled:bg-red-300"
            data-testid={testId ? `${testId}-submit` : undefined}
          >
            {busy ? "Eliminando..." : "Eliminar"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
